import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App'
import router from './router'
import moment from 'moment'
import * as Sentry from '@sentry/vue'
import 'nestable2/jquery.nestable.scss'
import '../scss/style.scss'

// import ElementPlus from 'element-plus'
// import 'element-plus/dist/index.css'
import { getStaticBase, toStaticAsset } from '@/utility/staticAsset'
import '@/utility/perfLogger'

moment.locale('da')
moment.updateLocale('da', {
    relativeTime: {
        future: 'om %s',
        past: '%s siden',
        s: 'få sekunder',
        ss: '%d sekunder',
        m: 'et minut',
        mm: '%d minutter',
        h: 'en time',
        hh: '%d timer',
        d: 'en dag',
        dd: '%d dage',
        w: 'en uge',
        ww: '%d uger',
        M: 'en måned',
        MM: '%d må. ',
        y: 'et år',
        yy: '%d år'
    }
})

const app = createApp(App)
const pinia = createPinia()

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_ID,
    environment: import.meta.env.MODE,
    release: __APP_SENTRY_RELEASE__,
    enabled: Boolean(import.meta.env.VITE_SENTRY_ID),
    beforeSend (event) {

        // Suppress noisy dynamic-import / chunk-load errors that are expected
        // during in-flight deploys or edge propagation. These errors cause
        // many duplicate Sentry events while we rely on client-side reload
        // logic to recover; ignore them here to reduce noise.
        const rawMessage = event?.exception?.values?.[0]?.value || event?.message || ''
        const dynamicImportNoise = /error loading dynamically imported module|Failed to fetch dynamically imported module|Loading chunk [\d]+ failed|ChunkLoadError/i

        if (dynamicImportNoise.test(String(rawMessage)))
            return null

        const release = __APP_SENTRY_RELEASE__
        const frames = event?.exception?.values?.[0]?.stacktrace?.frames || []
        event.tags = event.tags || {}
        event.extra = event.extra || {}

        if (frames.length > 0) {
            const inAppFrames = frames.filter(frame => frame?.in_app)
            const selectedFrame = inAppFrames[inAppFrames.length - 1] || frames[frames.length - 1]

            if (selectedFrame?.filename) {
                const normalizedFilename = String(selectedFrame.filename).replace(/\\/g, '/')
                const cleanFilename = normalizedFilename.split(/[?#]/)[0]
                const basename = cleanFilename.split('/').pop() || cleanFilename
                const line = selectedFrame?.lineno || 0
                const column = selectedFrame?.colno || 0

                if (!event.tags.where)
                    event.tags.where = `${basename}:${line}:${column}`

                if (!event.extra.raw_stack_frame)
                    event.extra.raw_stack_frame = `${cleanFilename}`

                if (!event.extra.raw_stack_frame_line_column)
                    event.extra.raw_stack_frame_line_column = `${line}:${column}`
            }
        }

        return event
    },
    ignoreErrors: [
        /canceled/i, 
        /Request aborted/i,
        /abort(ed|error)/i,
        /play()/,
        /load failed/i,
        /error loading dynamically imported module/i,
        /Failed to fetch dynamically imported module/i,
        /Loading chunk [\d]+ failed/i,
        /module script failed/i,
        /code 401/i,
        /non-error promise rejection/i,
        /operation was aborted/i,
        /maximum call stack size exceeded/i,
        /scrollheight/i,
        /getattribute/i,
        /classList/i,
        /connection lost/i
        // NOTE: 'network error' intentionally excluded — Axios throws this when the API
        // is unreachable, which IS a real error worth capturing.
    ],
    integrations: [
        Sentry.breadcrumbsIntegration({
            console: false
        })
        // To enable performance tracing, add: Sentry.browserTracingIntegration()
        //                              and set tracesSampleRate below (costs performance quota).
        // To enable session replays, add: Sentry.replayIntegration()
        // and set replaysSessionSampleRate / replaysOnErrorSampleRate below (costs replay quota).
    ]
    // tracesSampleRate: 0.1,       // uncomment + add browserTracingIntegration() to enable
    // replaysSessionSampleRate: 0, // uncomment + add replayIntegration() to enable
    // replaysOnErrorSampleRate: 1.0
})

// Global properties (replaces Vue.prototype)
app.config.globalProperties.moment = moment
app.config.globalProperties.$staticBase = getStaticBase()
app.config.globalProperties.$asset = toStaticAsset

app.use(pinia)
app.use(router)
// app.use(ElementPlus)
app.mount('#app')

const haaid = localStorage.getItem('haaid')
if (haaid) {
    Sentry.setUser({ id: haaid })
}